import React, { useState } from 'react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { SEO } from '../components/SEO';
import { contactInfo } from '../data/mock';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, Smartphone, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          subject: formData.subject,
          message: formData.message,
          source_url: window.location.href,
        }),
      });

      if (!response.ok) {
        throw new Error('Nachricht konnte nicht gesendet werden');
      }

      setIsSubmitted(true);
      toast.success('Nachricht erfolgreich gesendet!');
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO page="kontakt" />
      <Navbar />
      <main>
        {/* MOBILE: Klassischer Hero wie Dortmund (Bild oben, Titel darunter) */}
        <div className="md:hidden">
          {/* Vollbreites Bild */}
          <div className="w-full">
            <img 
              src="https://customer-assets.emergentagent.com/job_erste-hilfe-portal/artifacts/w6j4ajdd_0BFB3630-12B2-40B8-8C09-B8ADF81DD244.png"
              alt="Kontakt"
              className="w-full h-auto object-cover"
              style={{ aspectRatio: '16/9' }}
            />
          </div>
          
          {/* Titel-Bereich unter dem Bild */}
          <div className="bg-white px-4 py-6">
            <h1 className="text-2xl font-bold text-[#1e3a5f] leading-tight mb-3">
              Kontakt aufnehmen
            </h1>
            <p className="text-slate-600 text-base leading-relaxed">
              Wir sind für Sie da – per Telefon, E-Mail oder WhatsApp.
            </p>
          </div>
        </div>

        {/* DESKTOP: Bestehender Hero mit Gradient-Overlay (unverändert) */}
        <div className="hidden md:block">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div 
              className="relative overflow-hidden rounded-xl"
              style={{ 
                backgroundImage: `url("https://customer-assets.emergentagent.com/job_erste-hilfe-portal/artifacts/w6j4ajdd_0BFB3630-12B2-40B8-8C09-B8ADF81DD244.png")`,
                backgroundSize: '100% 100%',
                backgroundPosition: 'center center'
              }}
            >
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f]/90 via-[#1e3a5f]/70 to-[#1e3a5f]/20" />
              
              {/* Content - Einheitliche Höhe */}
              <div className="relative px-8 lg:px-10 py-12 lg:py-14">
                <div className="flex flex-row items-center justify-between gap-6 min-h-[240px]">
                  <div className="max-w-xl">
                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 text-white rounded-full text-sm font-semibold shadow-sm">
                        <MessageSquare className="w-3.5 h-3.5" />
                        Kontakt
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/95 text-[#1e3a5f] rounded-full text-sm font-semibold shadow-sm">
                        Mo–So erreichbar
                      </span>
                    </div>
                    
                    <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight drop-shadow-sm">
                      Kontakt aufnehmen
                    </h1>
                    
                    <p className="text-white/90 text-lg mt-3 drop-shadow-sm">
                      Wir sind für Sie da – per Telefon, E-Mail oder WhatsApp.
                    </p>
                    
                    <div className="mt-6 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                      <p className="text-white font-semibold text-lg">
                        Haben Sie Fragen zu unseren Kursen?
                      </p>
                      <p className="text-white/80 text-sm mt-1">
                        Rufen Sie uns an oder schreiben Sie uns – wir helfen Ihnen gerne weiter.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex-shrink-0 flex items-center">
                    <div className="bg-white/95 backdrop-blur-sm rounded-xl p-5 shadow-xl">
                      <div className="flex items-center gap-3 mb-3">
                        <Phone className="w-6 h-6 text-blue-600" />
                        <span className="font-semibold text-[#1e3a5f]">Direkter Kontakt</span>
                      </div>
                      <a href="tel:020419878666">
                        <Button className="w-full bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white mb-2">
                          02041 9878 666
                        </Button>
                      </a>
                      <a href="tel:017680151197">
                        <Button variant="outline" className="w-full border-[#1e3a5f] text-[#1e3a5f]">
                          0176 8015 1197
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <section className="bg-white">
          <div className="max-w-[1664px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
              {/* Contact Info */}
              <div className="bg-slate-50 rounded-xl p-6 lg:p-8 flex flex-col">
                <h2 className="text-2xl font-bold text-[#1e3a5f] mb-8">
                  So erreichen Sie uns
                </h2>
                
                <div className="space-y-6 flex-1">
                  {/* Adresse */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-red-600" />
                    </div>
                    <p className="text-slate-600">
                      {contactInfo.name}<br />
                      {contactInfo.address}<br />
                      {contactInfo.city}
                    </p>
                  </div>

                  {/* Festnetz */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-red-600" />
                    </div>
                    <a 
                      href={`tel:${contactInfo.phone}`} 
                      className="text-red-600 hover:text-red-700 transition-colors text-lg font-medium"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                  
                  {/* Mobil */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Smartphone className="w-6 h-6 text-red-600" />
                    </div>
                    <a 
                      href={`tel:${contactInfo.mobile}`} 
                      className="text-red-600 hover:text-red-700 transition-colors text-lg font-medium"
                    >
                      {contactInfo.mobile}
                    </a>
                  </div>

                  {/* E-Mail */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-red-600" />
                    </div>
                    <a 
                      href={`mailto:${contactInfo.email}`} 
                      className="text-red-600 hover:text-red-700 transition-colors text-lg font-medium"
                    >
                      {contactInfo.email}
                    </a>
                  </div>

                  {/* Erreichbarkeit */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1e3a5f] mb-1">Telefonisch erreichbar</h3>
                      <p className="text-slate-600">
                        Montag – Sonntag, 09:00 – 17:00 Uhr
                      </p>
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <div className="flex items-start gap-4 bg-[#25D366]/10 p-4 rounded-xl">
                    <div className="w-12 h-12 bg-[#25D366] rounded-xl flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1e3a5f] mb-1">WhatsApp</h3>
                      <p className="text-slate-600 text-sm mb-3">
                        Schnelle Antwort per WhatsApp
                      </p>
                      <a 
                        href="https://wa.me/4917680151197?text=Hallo%20Erste%20Hilfe%20Bottrop%2C%20ich%20habe%20eine%20Frage%20zu%20den%20Erste-Hilfe-Kursen%20(Samstags%20%26%20Sonntags).%20Bitte%20um%20R%C3%BCckruf%2FInfos.%20Danke!"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BD5A] text-white px-4 py-2 rounded-lg font-medium transition-colors"
                        data-testid="whatsapp-contact-btn"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        WhatsApp Chat starten
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="flex flex-col">
                <Card className="border-0 shadow-xl flex-1 flex flex-col">
                  <CardHeader>
                    <CardTitle className="text-2xl text-[#1e3a5f]">Nachricht senden</CardTitle>
                  </CardHeader>
                  <CardContent className="flex-1 flex flex-col justify-between">
                    {isSubmitted ? (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-[#1e3a5f] mb-2">
                          Nachricht gesendet!
                        </h3>
                        <p className="text-slate-600 mb-6">
                          Vielen Dank für Ihre Nachricht. Wir melden uns schnellstmöglich bei Ihnen.
                        </p>
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setIsSubmitted(false);
                            setFormData({
                              name: '',
                              email: '',
                              phone: '',
                              subject: '',
                              message: '',
                            });
                          }}
                        >
                          Neue Nachricht
                        </Button>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                          <Label htmlFor="name">Name *</Label>
                          <Input 
                            id="name" 
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required 
                            placeholder="Ihr vollständiger Name"
                          />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="email">E-Mail *</Label>
                            <Input 
                              id="email" 
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required 
                              placeholder="ihre@email.de"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone">Telefon</Label>
                            <Input 
                              id="phone" 
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleInputChange}
                              placeholder="+49 123 456789"
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="subject">Betreff *</Label>
                          <Input 
                            id="subject" 
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            required 
                            placeholder="Worum geht es?"
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="message">Nachricht *</Label>
                          <Textarea 
                            id="message" 
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            required 
                            placeholder="Ihre Nachricht an uns..."
                            rows={5}
                          />
                        </div>

                        <Button 
                          type="submit" 
                          className="w-full bg-red-600 hover:bg-red-700 h-12 text-base"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            'Wird gesendet...'
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Nachricht senden
                            </>
                          )}
                        </Button>
                      </form>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
